import React from 'react';
import Tokens from '@nokia-csf-uxr/freeform-design-tokens';
import _merge from 'lodash/merge';
import { getContext } from 'common';
import Typography, { TypographyProps } from 'components/Typography';

import Styled from './styles';

const TYPOGRAPHY_CONFIG = {
  maxLines: 1,
  variant: 'TITLE_16',
  style: {
    flex: 1,
    marginTop: Tokens.SPACING.SPACING_16,
    marginBottom: Tokens.SPACING.SPACING_16
  }
};

export interface OverlayPanelHeaderProps extends Omit<React.ComponentPropsWithRef<'div'>, 'title'> {
  /** The content of the header. */
  title: React.ReactNode;
  /** The title's `<Typography />` props. Note: Styling and theming of the content are handled by `<Typography />` and the background is transparent. */
  typographyProps?: Partial<TypographyProps>;
  /** The header's buttons, consider using `<IconButton />` */
  buttons?: React.ReactNode;
  /** To override the theme please refer to the "Readme" tab to learn more. */
  theme?: any;
}

/** `<OverlayPanelHeader />` can be used to render header in `<OverlayPanel />`. All other props will be provided to the wrapper element. */
const OverlayPanelHeader = React.forwardRef<HTMLElement, OverlayPanelHeaderProps>((props: OverlayPanelHeaderProps, ref) => getContext(({ RTL, theme }) => {
  const {
    title,
    theme: themeProp,
    buttons,
    typographyProps: givenTypographyProps,
    ...otherProps
  } = props;

  const resolvedTheme = _merge({}, theme ? theme.OVERLAY_PANEL : {}, themeProp);

  const typographyProps = _merge(
    {}, {
      style: {
        color: resolvedTheme.title,
      },
    },
    TYPOGRAPHY_CONFIG,
    givenTypographyProps
  );

  return (
    <Styled.Header
      $rtl={RTL}
      ref={ref}
      {...otherProps}
    >
      <Typography {...typographyProps} >{title}</Typography>
      <Styled.ButtonsContainer $rtl={RTL}>{buttons}</Styled.ButtonsContainer>
    </Styled.Header>
  );
}));

OverlayPanelHeader.displayName = 'OverlayPanelHeader';

OverlayPanelHeader.defaultProps = {
  buttons: null,
  typographyProps: {},
  theme: {}
};

export default OverlayPanelHeader;
